import { forwardRef, useEffect, useMemo, useRef, useState } from "react";

import { cn, isDev, platformProps } from "../../lib";
import { Box, composeRefs, Pressable, TextInput } from "../../primitives";
import { Icon } from "../icons";
import {
  getTextFieldVisualState,
  textFieldClearIconClass,
  textFieldIconClass,
  textFieldIconSizeMap,
  textFieldInputClass,
  textFieldInputReachClass,
  textFieldPlaceholderClass,
  textFieldTrailingHitSlop,
  textFieldTrailingTouchClass,
} from "./lib";
import { textFieldContainerVariants } from "./text-field.variants";
import type { TextFieldProps } from "./types";

export const TextField = forwardRef<React.ElementRef<typeof TextInput>, TextFieldProps>(
  (
    {
      size = "sm",
      state = "default",
      variant = "default",
      disabled = false,
      leadingIcon,
      trailingIcon,
      iconWeight = "regular",
      onTrailingIconPress,
      trailingIconLabel,
      className,
      inputClassName,
      testID,
      id,
      onFocus,
      onBlur,
      ...textInputProps
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<React.ElementRef<typeof TextInput>>(null);
    const setRefs = useMemo(() => composeRefs(ref, inputRef), [ref]);
    const unnamedAction = Boolean(
      trailingIcon && onTrailingIconPress && !trailingIconLabel?.trim(),
    );
    useEffect(() => {
      if (unnamedAction && isDev()) {
        console.error(
          'TextField: a trailing action needs `trailingIconLabel`, the name of what it does ("Show password", "Clear weight"). Without it the button has no name for screen readers.',
        );
      }
    }, [unnamedAction]);

    const visualState = getTextFieldVisualState({ disabled, isFocused, state });
    const iconClass = textFieldIconClass[visualState];
    const iconSize = textFieldIconSizeMap[size];

    const isSearch = variant === "search";
    const leading = leadingIcon ?? (isSearch ? "MagnifyingGlass" : undefined);
    const { value, readOnly, onChangeText } = textInputProps;
    // The clear × clears through `onChangeText`, so it needs a controlled value
    const showClear =
      isSearch &&
      !trailingIcon &&
      typeof value === "string" &&
      value.length > 0 &&
      !disabled &&
      !readOnly &&
      Boolean(onChangeText);
    const clear = () => {
      onChangeText?.("");
      inputRef.current?.focus();
    };

    return (
      <Box
        {...platformProps({ slot: "text-field" })}
        // Doesn't clip, so the trailing action's tap area can pass the edge.
        // No vertical padding: the input fills the field's height, so a tap
        // anywhere above or below the text line still reaches it (the field's
        // height is fixed, so the text sits where it did)
        className={cn(
          textFieldContainerVariants({ size, visualState, variant }),
          "md:py-spacing-0 py-spacing-0",
          "overflow-visible",
          className,
        )}
      >
        {leading && (
          <Icon
            name={leading}
            size={iconSize}
            weight={iconWeight}
            className={cn("shrink-0", iconClass)}
          />
        )}

        <TextInput
          ref={setRefs}
          {...platformProps({ testID, id })}
          disabled={disabled}
          className={cn(
            // min-w-0: a web <input> otherwise keeps its intrinsic width and
            // overflows a narrow field
            "text-body-md md:text-body-md-dt native:font-inter-regular min-w-0 flex-1 self-stretch bg-transparent outline-none",
            // A small field's input takes taps 44 tall (Form #18)
            textFieldInputReachClass[size],
            textFieldInputClass[visualState],
            textFieldPlaceholderClass[visualState],
            inputClassName,
          )}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          {...textInputProps}
          invalid={state === "error"}
        />

        {trailingIcon &&
          (onTrailingIconPress ? (
            <Pressable
              onClick={onTrailingIconPress}
              disabled={disabled}
              hitSlop={textFieldTrailingHitSlop[size]}
              className={cn("shrink-0", textFieldTrailingTouchClass[size])}
              accessibilityLabel={trailingIconLabel}
            >
              <Icon name={trailingIcon} size={iconSize} weight={iconWeight} className={iconClass} />
            </Pressable>
          ) : (
            <Icon
              name={trailingIcon}
              size={iconSize}
              weight={iconWeight}
              className={cn("shrink-0", iconClass)}
            />
          ))}

        {showClear && (
          <Pressable
            onClick={clear}
            hitSlop={textFieldTrailingHitSlop[size]}
            className={cn("shrink-0", textFieldTrailingTouchClass[size])}
            accessibilityLabel={trailingIconLabel?.trim() || "Clear search"}
          >
            <Icon
              name="X"
              size={iconSize}
              weight={iconWeight}
              className={textFieldClearIconClass[visualState]}
            />
          </Pressable>
        )}
      </Box>
    );
  },
);

TextField.displayName = "TextField";
